import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, ChevronDown, RefreshCw, X, Ban, AlertTriangle,
  CheckSquare, Square, CalendarX, Check, CheckCircle2, CircleSlash, CalendarRange,
} from "lucide-react";
import { toLocalISODate } from "../../utils/date";
import { effectiveNightlyRate } from "../../utils/pricing";
import { SOURCE_CONFIG, OtaMark, BookingModal, canonicalSource } from "../../pages/CalendarPage";
import { bookingLabel } from "../../utils/bookingLabel";
import useRangeSelect from "../../utils/useRangeSelect";
import DayRatesSheet from "./DayRatesSheet";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const WINDOW_DAYS = 14;
const ROW_HEIGHT = 52;
const LABEL_WIDTH = 220;
const DAY_WIDTH = 92;

// Compact monogram shown on each bar, similar in spirit to a channel
// manager's OTA logo chip — real brand logos aren't bundled here, so this
// uses the same source colors already established elsewhere in the app
// instead of trademarked artwork.

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

// Where a booking's stay overlaps the visible window, as day-column
// indices (checkOut is exclusive, same convention as the month grid).
function overlapIndices(booking, windowDates) {
  const ci = startOfDay(booking.checkIn);
  const co = startOfDay(booking.checkOut);
  let start = -1, end = -1;
  windowDates.forEach((d, i) => {
    if (d >= ci && d < co) { if (start === -1) start = i; end = i; }
  });
  return start === -1 ? null : { start, end };
}

// Pill badge for an availability count — green check when rooms are free,
// red slash when fully sold out, matching the channel-manager reference
// this view is modeled on.
function AvailPill({ n, total }) {
  const soldOut = n === 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full
      ${soldOut ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
      {soldOut ? <CircleSlash size={11} /> : <CheckCircle2 size={11} />}
      {soldOut ? "SOLD OUT" : `${n} AVL`}
    </span>
  );
}

/* ── Confirm-and-block popover, shown after a drag selection ── */
function BlockConfirmModal({ label, dateFrom, dateTo, onClose, onConfirm, saving, error }) {
  const [reason, setReason] = useState("");
  const nights = Math.round((dateTo - dateFrom) / 86400000);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Ban size={16} className="text-gray-500" /> Block {label}
          </h3>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-600">
            {dateFrom.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            {" – "}
            {dateTo.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            {" "}<span className="text-gray-400">({nights} night{nights !== 1 ? "s" : ""})</span>
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason (optional)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Maintenance, owner use"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-60">
            {saving ? "Blocking…" : "Block dates"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Block a date range across several selected rooms at once — the
   drag gesture is naturally one-room-at-a-time, so bulk blocking (e.g.
   "close rooms 4, 5 and 7 for renovation next week") goes through a plain
   date-range form instead, fired as one request per room. ── */
function MultiBlockModal({ rows, onClose, onConfirm, saving, error }) {
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [reason, setReason] = useState("");
  const todayIso = toLocalISODate(new Date());
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <CalendarX size={16} className="text-gray-500" /> Block {rows.length} room{rows.length !== 1 ? "s" : ""}
          </h3>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {rows.map((r) => (
              <span key={r.unitId} className="text-[11px] font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {r.roomNumber ? `#${r.roomNumber} ` : ""}{r.typeName}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Check-in</label>
              <input type="date" value={checkIn} min={todayIso} onChange={(e) => setCheckIn(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Check-out</label>
              <input type="date" value={checkOut} min={checkIn || todayIso} onChange={(e) => setCheckOut(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason (optional)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Renovation"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          {error && <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => checkIn && checkOut && onConfirm(checkIn, checkOut, reason)}
            disabled={saving || !checkIn || !checkOut}
            className="flex-1 py-2.5 rounded-xl bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-60"
          >
            {saving ? "Blocking…" : "Block dates"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Type header row — collapsible, with a per-type AVL/SOLD OUT summary
   for each visible date so the property-wide "Total available" row above
   isn't the only availability signal. ── */
function TypeHeaderRow({ typeName, roomCount, windowDates, availPerDay, expanded, onToggle, unassignedCount }) {
  return (
    <div className="flex bg-gray-100/80" style={{ height: ROW_HEIGHT }}>
      <button
        type="button"
        onClick={onToggle}
        className="flex-shrink-0 flex items-center gap-2 px-4 border-r border-b border-gray-200 sticky left-0 z-10 bg-gray-100/80 hover:bg-gray-200/60 transition-colors text-left"
        style={{ width: LABEL_WIDTH }}
      >
        {expanded ? <ChevronDown size={14} className="text-gray-500 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-500 flex-shrink-0" />}
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="block text-[13px] font-bold text-gray-800 truncate">{typeName}</span>
            {!!unassignedCount && (
              <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" title={`${unassignedCount} booking(s) need a room number`} />
            )}
          </span>
          <span className="block text-[10.5px] text-gray-400">{roomCount} room{roomCount !== 1 ? "s" : ""}</span>
        </span>
      </button>
      <div className="grid border-b border-gray-200" style={{ gridTemplateColumns: `repeat(${windowDates.length}, ${DAY_WIDTH}px)` }}>
        {availPerDay.map((n, i) => (
          <div key={i} className="flex items-center justify-center border-r border-gray-100">
            <AvailPill n={n} total={roomCount} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── The nightly rate for one room type, directly under its header so a
   date column reads top-to-bottom as: how many left, what it costs, who is
   in. Computed with the SAME effectiveNightlyRate the public booking page
   and the backend price with, from rateOverrides already present on the room
   documents this view loads — no extra request, and it cannot drift from
   what a guest is actually quoted.

   Each price is also the way to change it: tap one night, or drag across
   several (press and hold first on a phone), and the rates panel opens for
   this room type and those nights. ── */
function TypeRateRow({ room, windowDates, range, onOpen }) {
  const base = room.pricePerNight || 0;
  const group = `rate-${room._id}`;
  return (
    <div className="flex bg-white/60" style={{ height: 45 }}>
      <div className="flex-shrink-0 flex items-center px-4 border-r border-b border-gray-200 sticky left-0 z-10 bg-white/90"
        style={{ width: LABEL_WIDTH }}>
        <span className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider">Rate / night</span>
      </div>
      <div className="grid border-b border-gray-200" style={{ gridTemplateColumns: `repeat(${windowDates.length}, ${DAY_WIDTH}px)` }}>
        {windowDates.map((d, i) => {
          const ymd = toLocalISODate(d);
          const rate = effectiveNightlyRate(room, ymd);
          const overridden = rate !== base;
          const picked = range.isSelected(group, ymd);
          return (
            <button key={i} type="button" onClick={() => onOpen(room._id, ymd, 1)}
              aria-label={`Change the ${room.name || ""} rate on ${d.toLocaleDateString("en-IN", { day: "numeric", month: "long" })}`}
              {...range.bind(group, ymd)}
              style={{ WebkitTouchCallout: "none" }}
              className={`flex items-center justify-center border-r border-gray-100 select-none transition-colors
                ${picked ? "bg-amber-100 ring-2 ring-inset ring-amber-400" : "hover:bg-amber-50/70"}`}>
              <span className={`text-[11.5px] font-semibold tabular-nums px-1.5 py-0.5 rounded
                ${overridden ? "bg-amber-50 text-amber-700" : "text-gray-500"}`}>
                {rate ? `₹${rate.toLocaleString("en-IN")}` : "—"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── One room's row: a strip of day cells (mousedown targets for the
   drag-to-block gesture) with booking/blocked bars layered on top. ── */
function RoomRow({ room, bookings, windowDates, todayIdx, drag, onCellDown, onCellEnter, onBarClick, isBookedAt, blockable = true, striped = false, warn = false, compact = false, selectMode = false, selected = false, onToggleSelect }) {
  const bars = useMemo(() => {
    return bookings
      .map((b) => {
        const span = overlapIndices(b, windowDates);
        return span && { booking: b, ...span };
      })
      .filter(Boolean);
  }, [bookings, windowDates]);

  const dragForThisRoom = drag && drag.rowId === room._id ? drag : null;

  return (
    <div className={`flex ${striped ? "bg-gray-50/40" : ""}`} style={{ height: ROW_HEIGHT }}>
      <div
        className={`flex-shrink-0 flex items-center gap-2.5 px-4 border-r border-b border-gray-100 sticky left-0 z-10 ${warn ? "bg-amber-50" : striped ? "bg-gray-50/40" : "bg-white"}`}
        style={{ width: LABEL_WIDTH, paddingLeft: compact ? 34 : 16 }}
      >
        {selectMode && blockable && (
          <button type="button" onClick={() => onToggleSelect(room._id)} className="flex-shrink-0 text-gray-400 hover:text-amber-600">
            {selected ? <CheckSquare size={16} className="text-amber-500" /> : <Square size={16} />}
          </button>
        )}
        {warn ? (
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
        ) : compact ? (
          <>
            <Check size={13} className="text-emerald-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-800">
              {room.roomNumber || <span className="text-gray-300">?</span>}
            </span>
          </>
        ) : room.roomNumber ? (
          <span className="w-7 h-7 rounded-lg bg-gray-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
            {room.roomNumber}
          </span>
        ) : (
          <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-300 text-xs font-bold flex items-center justify-center flex-shrink-0" title="No room number set">
            ?
          </span>
        )}
        {!compact && (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{room.name}</p>
            <p className="text-[11px] text-gray-400 truncate">{room.type}{room.capacity ? ` · ${room.capacity} guests` : ""}</p>
          </div>
        )}
      </div>
      <div className="relative border-b border-gray-100" style={{ width: DAY_WIDTH * windowDates.length }}>
        {/* Click/drag targets, one per day */}
        <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${windowDates.length}, ${DAY_WIDTH}px)` }}>
          {windowDates.map((d, i) => {
            const booked = !blockable || selectMode || isBookedAt(room._id, i);
            return (
              <button
                key={i}
                type="button"
                disabled={booked}
                onMouseDown={() => blockable && !selectMode && !booked && onCellDown(room._id, i)}
                onMouseEnter={() => blockable && !selectMode && onCellEnter(room._id, i)}
                className={`border-r h-full ${i === todayIdx ? "bg-amber-50/50 border-amber-100" : "border-gray-50"}
                  ${blockable && !booked ? "cursor-pointer hover:bg-amber-50/70" : "cursor-default"}`}
              />
            );
          })}
        </div>
        {/* Drag-in-progress highlight */}
        {dragForThisRoom && (
          <div
            className="absolute top-1.5 bottom-1.5 bg-amber-200/70 border border-amber-400 rounded-md pointer-events-none"
            style={{
              left: Math.min(dragForThisRoom.startIdx, dragForThisRoom.endIdx) * DAY_WIDTH + 2,
              width: (Math.abs(dragForThisRoom.endIdx - dragForThisRoom.startIdx) + 1) * DAY_WIDTH - 4,
            }}
          />
        )}
        {/* Booking / blocked bars */}
        {bars.map(({ booking, start, end }) => {
          const cfg = SOURCE_CONFIG[canonicalSource(booking.source)] || SOURCE_CONFIG.Manual;
          const isBlocked = booking.status === "Blocked";
          // A checked-out (or no-show) stay is history: it no longer holds the
          // room, so it must not look identical to one that does. Faded and
          // outlined rather than hidden — staff still want to see who was in
          // the room, they just shouldn't read it as "unavailable".
          const isDone = !occupiesRoom(booking);
          const label = bookingLabel(booking);
          return (
            <button
              key={booking._id}
              type="button"
              onClick={() => onBarClick(booking)}
              title={`${booking.source} · ${label}${isDone ? ` · ${booking.status} — room is free` : ""}`}
              className={`absolute top-1.5 bottom-1.5 rounded-lg pl-1 pr-2.5 flex items-center gap-1.5 text-[11.5px] font-medium truncate text-left shadow-sm transition-all hover:shadow hover:brightness-95 border-b-2
                ${isDone
                  ? "bg-white/70 text-gray-400 border-gray-200 border border-dashed shadow-none"
                  : isBlocked ? "bg-gray-200 text-gray-700 border-gray-400" : `${cfg.color} text-white border-black/20`}`}
              style={{ left: start * DAY_WIDTH + 2, width: (end - start + 1) * DAY_WIDTH - 4 }}
            >
              {isBlocked ? (
                <span className="w-5 h-5 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
                  <Ban size={11} className="text-gray-600" />
                </span>
              ) : (
                <span className="flex items-center justify-center flex-shrink-0">
                  {/* The channel's own badge, so a bar says where the booking
                      came from without reading the tooltip. */}
                  <OtaMark source={booking.source} size={22} />
                </span>
              )}
              <span className="truncate">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


/* Does this booking still hold its room?
   Mirrors ACTIVE_STATUSES in backend/services/bookingOverlap.js — the two run
   in separate runtimes, so keep them in step.

   The calendar endpoint returns everything except Cancelled (so a finished
   stay stays visible as history, which is useful), but "visible" and "holds
   the room" are different questions and were being answered by the same
   check. A guest who checked out early left their room showing blocked for
   the rest of the original range, and the Inventory tab — which asks the
   backend, and gets this right — disagreed with the Bookings tab on the very
   same dates. */
const OCCUPYING_STATUSES = new Set(["Confirmed", "Pending", "Blocked"]);
function occupiesRoom(b) {
  return OCCUPYING_STATUSES.has(b?.status);
}

export default function CalendarListView({ authHeaders }) {
  const [rooms, setRooms] = useState([]); // room TYPE documents, each with units[]
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowStart, setWindowStart] = useState(startOfDay(new Date()));
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null); // { rowId (=unitId), startIdx, endIdx }
  const [confirmBlock, setConfirmBlock] = useState(null); // { roomId, unitId, label, dateFrom, dateTo }
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // unit ids
  const [showMultiBlock, setShowMultiBlock] = useState(false);
  const [multiBlocking, setMultiBlocking] = useState(false);
  const [multiBlockError, setMultiBlockError] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set()); // type names collapsed
  const [rateOpen, setRateOpen] = useState(null); // { roomId, date, nights }
  const containerRef = useRef(null);
  const openRates = (roomId, date, nights) => setRateOpen({ roomId, date, nights });
  const rateRange = useRangeSelect((group, first, last) => openRates(group.slice("rate-".length), first,
    Math.round((new Date(last) - new Date(first)) / 86400000) + 1));

  function toggleSelect(unitId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(unitId) ? next.delete(unitId) : next.add(unitId);
      return next;
    });
  }
  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }
  function toggleType(typeName) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(typeName) ? next.delete(typeName) : next.add(typeName);
      return next;
    });
  }

  const windowDates = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, i) => addDays(windowStart, i)),
    [windowStart]
  );
  const todayIdx = windowDates.findIndex((d) => toLocalISODate(d) === toLocalISODate(new Date()));

  const load = useCallback(async () => {
    setLoading(true);
    const from = toLocalISODate(windowStart);
    const to = toLocalISODate(addDays(windowStart, WINDOW_DAYS));
    try {
      const [bRes, rRes] = await Promise.all([
        fetch(`${API}/calendar?from=${from}&to=${to}`, { headers: authHeaders() }),
        fetch(`${API}/rooms`, { headers: authHeaders() }),
      ]);
      const bJson = await bRes.json();
      const rJson = await rRes.json();
      setBookings(bJson.data || []);
      setRooms((rJson.data || []).filter((r) => r.active !== false));
    } catch {}
    setLoading(false);
  }, [windowStart, authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Each room TYPE's active physical rooms, flattened with the metadata
  // every row/lookup below needs — built once per rooms/bookings change
  // instead of re-deriving it in several places.
  const types = useMemo(
    () => rooms.map((r) => ({
      ...r,
      activeUnits: (r.units || []).filter((u) => u.active !== false),
    })),
    [rooms]
  );
  const allUnitIds = useMemo(() => types.flatMap((t) => t.activeUnits.map((u) => u._id)), [types]);
  // unitId -> which type it belongs to, for turning a row click back into
  // the {roomId, unitId} pair the block API expects.
  const unitMeta = useMemo(() => {
    const m = {};
    types.forEach((t) => t.activeUnits.forEach((u) => {
      m[u._id] = { roomId: t._id, roomNumber: u.roomNumber, typeName: t.name };
    }));
    return m;
  }, [types]);

  // Drag-select ends wherever the mouse comes up, even outside a cell.
  useEffect(() => {
    function onMouseUp() {
      setDrag((d) => {
        if (!d) return null;
        const meta = unitMeta[d.rowId];
        if (meta) {
          const lo = Math.min(d.startIdx, d.endIdx);
          const hi = Math.max(d.startIdx, d.endIdx);
          setConfirmBlock({
            roomId: meta.roomId,
            unitId: d.rowId,
            label: `${meta.typeName}${meta.roomNumber ? ` #${meta.roomNumber}` : ""}`,
            dateFrom: windowDates[lo],
            dateTo: addDays(windowDates[hi], 1),
          });
        }
        return null;
      });
    }
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [unitMeta, windowDates]);

  // Bookings not tied to any specific unit used to just vanish from this
  // view since it's keyed by row — that hid real reservations from the
  // "which rooms are free" picture instead of surfacing them. Two cases:
  // no room at all (property-wide/unknown), or a room type but no unit yet
  // (pre-migration bookings, or an OTA sync that hasn't been re-run). Both
  // get their own row instead of disappearing.
  const { bookingsByUnit, typeUnassigned, unassigned } = useMemo(() => {
    const byUnit = {};
    const byType = {};
    const loose = [];
    bookings.forEach((b) => {
      const rid = b.room?._id || b.room;
      if (!rid) { loose.push(b); return; }
      if (b.unitId) { (byUnit[b.unitId] ||= []).push(b); return; }
      (byType[rid] ||= []).push(b);
    });
    return { bookingsByUnit: byUnit, typeUnassigned: byType, unassigned: loose };
  }, [bookings]);

  function isBookedAt(unitId, dayIdx) {
    const list = bookingsByUnit[unitId] || [];
    const d = windowDates[dayIdx];
    return list.some((b) => occupiesRoom(b) && d >= startOfDay(b.checkIn) && d < startOfDay(b.checkOut));
  }

  function onCellDown(unitId, dayIdx) {
    setDrag({ rowId: unitId, startIdx: dayIdx, endIdx: dayIdx });
  }
  function onCellEnter(unitId, dayIdx) {
    setDrag((d) => {
      if (!d || d.rowId !== unitId) return d;
      // Don't let the selection jump over an already-booked cell.
      const lo = Math.min(d.startIdx, dayIdx);
      const hi = Math.max(d.startIdx, dayIdx);
      for (let i = lo; i <= hi; i++) {
        if (isBookedAt(unitId, i)) return d;
      }
      return { ...d, endIdx: dayIdx };
    });
  }

  async function confirmAndBlock(reason) {
    setBlocking(true);
    setBlockError("");
    try {
      const res = await fetch(`${API}/bookings/block`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          roomId: confirmBlock.roomId,
          unitId: confirmBlock.unitId,
          checkIn: toLocalISODate(confirmBlock.dateFrom),
          checkOut: toLocalISODate(confirmBlock.dateTo),
          reason,
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      setConfirmBlock(null);
      load();
    } catch (e) {
      setBlockError(e.message);
    } finally {
      setBlocking(false);
    }
  }

  async function confirmMultiBlock(checkIn, checkOut, reason) {
    setMultiBlocking(true);
    setMultiBlockError("");
    try {
      const results = await Promise.all([...selectedIds].map((unitId) => {
        const meta = unitMeta[unitId];
        return fetch(`${API}/bookings/block`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ roomId: meta.roomId, unitId, checkIn, checkOut, reason }),
        }).then((r) => r.json());
      }));
      const failed = results.filter((r) => !r.success);
      if (failed.length) throw new Error(failed.map((f) => f.error).join("; "));
      setShowMultiBlock(false);
      exitSelectMode();
      load();
    } catch (e) {
      setMultiBlockError(e.message);
    } finally {
      setMultiBlocking(false);
    }
  }

  async function onBarClick(booking) {
    if (booking.status !== "Blocked") { setSelected(booking); return; }
    // The label is a noun for a timeline bar, so it only reads well here as an
    // aside — "Unblock Blocked on Airbnb on this room?" is not a sentence.
    // A bare "Blocked" adds nothing over the verb, so drop it in that case.
    const why = /^blocked$/i.test(bookingLabel(booking)) ? "" : ` (${bookingLabel(booking)})`;
    if (!window.confirm(`Unblock these dates${why} on this room? The dates will become available again.`)) return;
    try {
      await fetch(`${API}/bookings/${booking._id}/cancel`, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: "{}" });
      load();
    } catch {}
  }

  const rangeLabel = `${windowDates[0].toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} - ${windowDates[WINDOW_DAYS - 1].toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`;

  const availPerDay = windowDates.map((_, i) => allUnitIds.filter((id) => !isBookedAt(id, i)).length);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Branded header strip, styled after the reference channel-manager
          list view — kept in this app's amber brand rather than an
          unrelated blue so it reads as part of the same product. No title
          text here: the page above already says "Availability Calendar",
          so a second heading would just repeat it. */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3.5 flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs font-bold text-white/90 uppercase tracking-wider">Showing</span>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 rounded-lg px-3 py-1.5">
            <CalendarRange size={13} /> {rangeLabel}
          </span>
          <button onClick={() => setWindowStart(startOfDay(new Date()))}
            className="text-xs font-bold text-amber-700 bg-white rounded-lg px-3 py-1.5 hover:bg-amber-50">
            Today
          </button>
          <button onClick={load} className="w-11 h-11 flex items-center justify-center hover:bg-white/15 rounded-lg">
            <RefreshCw size={15} className={`text-white ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 flex-wrap gap-3">
        <p className="text-xs text-gray-400">
          {allUnitIds.length} room{allUnitIds.length !== 1 ? "s" : ""} · {types.length} type{types.length !== 1 ? "s" : ""} ·{" "}
          {selectMode ? "tick rooms, then block a date range for all of them" : "click and drag empty dates on a row to block one room · tap or drag a rate to change it"}
        </p>
        <div className="flex items-center gap-2">
          {selectMode ? (
            <>
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => setShowMultiBlock(true)}
                disabled={selectedIds.size === 0}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-gray-800 hover:bg-gray-900
                  rounded-lg px-3 py-2 disabled:opacity-40 transition-colors"
              >
                <CalendarX size={13} /> Block selected
              </button>
              <button onClick={exitSelectMode} className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setSelectMode(true)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
                <CheckSquare size={13} /> Block multiple rooms
              </button>
              <button onClick={() => setWindowStart((d) => addDays(d, -WINDOW_DAYS))} className="p-2 hover:bg-gray-100 rounded-xl">
                <ChevronLeft size={18} className="text-gray-500" />
              </button>
              <button onClick={() => setWindowStart((d) => addDays(d, WINDOW_DAYS))} className="p-2 hover:bg-gray-100 rounded-xl">
                <ChevronRight size={18} className="text-gray-500" />
              </button>
            </>
          )}
        </div>
      </div>

      {allUnitIds.length === 0 && !loading ? (
        <p className="text-sm text-gray-400 text-center py-12">No active rooms yet — add rooms first.</p>
      ) : (
        <div ref={containerRef} className="overflow-x-auto select-none">
          <div style={{ width: LABEL_WIDTH + DAY_WIDTH * WINDOW_DAYS }}>
            {/* Date header */}
            <div className="flex sticky top-0 bg-white z-20 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
              <div className="flex-shrink-0 flex items-center px-4 border-r border-b border-gray-100 sticky left-0 bg-white z-10" style={{ width: LABEL_WIDTH }}>
                <p className="text-[11px] font-black text-gray-500 uppercase tracking-wider">Total</p>
              </div>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${WINDOW_DAYS}, ${DAY_WIDTH}px)` }}>
                {windowDates.map((d, i) => (
                  <div key={i} className={`text-center py-2 border-r border-b border-gray-100 ${i === todayIdx ? "bg-amber-50" : ""}`}>
                    <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider">{d.toLocaleDateString("en-IN", { weekday: "short" })}</p>
                    <p className={`text-[13px] font-semibold leading-tight tabular-nums ${i === todayIdx ? "text-amber-600" : "text-gray-700"}`}>{d.getDate()}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Total available row */}
            <div className="flex bg-gray-50/70">
              <div className="flex-shrink-0 flex items-center px-4 border-r border-b border-gray-100 sticky left-0 bg-gray-50/70 z-10" style={{ width: LABEL_WIDTH }}>
                <p className="text-xs font-semibold text-gray-500">Whole property</p>
              </div>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${WINDOW_DAYS}, ${DAY_WIDTH}px)` }}>
                {availPerDay.map((n, i) => (
                  <div key={i} className="flex items-center justify-center border-r border-b border-gray-100 py-1.5">
                    <AvailPill n={n} total={allUnitIds.length} />
                  </div>
                ))}
              </div>
            </div>

            {/* Bookings with no room at all — never silently hidden */}
            {unassigned.length > 0 && (
              <RoomRow
                room={{ _id: "__unassigned", name: "No room assigned", type: `${unassigned.length} booking${unassigned.length !== 1 ? "s" : ""} need a room` }}
                bookings={unassigned}
                windowDates={windowDates}
                todayIdx={todayIdx}
                drag={drag}
                onCellDown={() => {}}
                onCellEnter={() => {}}
                onBarClick={onBarClick}
                isBookedAt={() => false}
                blockable={false}
                warn
              />
            )}

            {/* Room types, each collapsible, with its own AVL summary row */}
            {types.map(({ _id: typeId, name, type, capacity, activeUnits, rateOverrides, pricePerNight }, gi) => {
              const isCollapsed = collapsed.has(name);
              const unitIds = activeUnits.map((u) => u._id);
              const typeAvailPerDay = windowDates.map((_, i) => unitIds.filter((id) => !isBookedAt(id, i)).length);
              const looseForType = typeUnassigned[typeId] || [];
              return (
                <div key={typeId}>
                  <TypeHeaderRow
                    typeName={name}
                    roomCount={activeUnits.length}
                    windowDates={windowDates}
                    availPerDay={typeAvailPerDay}
                    expanded={!isCollapsed}
                    onToggle={() => toggleType(name)}
                    unassignedCount={looseForType.length}
                  />
                  {!isCollapsed && (
                    <TypeRateRow room={{ _id: typeId, name, rateOverrides, pricePerNight }} windowDates={windowDates}
                      range={rateRange} onOpen={openRates} />
                  )}
                  {!isCollapsed && looseForType.length > 0 && (
                    <RoomRow
                      room={{ _id: `__unassigned_${typeId}`, name: `${name} — no room number`, type: `${looseForType.length} booking${looseForType.length !== 1 ? "s" : ""} need a room number` }}
                      bookings={looseForType}
                      windowDates={windowDates}
                      todayIdx={todayIdx}
                      drag={drag}
                      onCellDown={() => {}}
                      onCellEnter={() => {}}
                      onBarClick={onBarClick}
                      isBookedAt={() => false}
                      blockable={false}
                      warn
                    />
                  )}
                  {!isCollapsed && activeUnits.map((unit, i) => (
                    <RoomRow
                      key={unit._id}
                      room={{ _id: unit._id, name, type, capacity, roomNumber: unit.roomNumber }}
                      bookings={bookingsByUnit[unit._id] || []}
                      windowDates={windowDates}
                      todayIdx={todayIdx}
                      drag={drag}
                      onCellDown={onCellDown}
                      onCellEnter={onCellEnter}
                      onBarClick={onBarClick}
                      isBookedAt={isBookedAt}
                      striped={i % 2 === 1}
                      compact
                      selectMode={selectMode}
                      selected={selectedIds.has(unit._id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Source legend */}
      <div className="flex flex-wrap gap-4 px-6 py-3.5 border-t border-gray-100 bg-gray-50/50">
        {Object.entries(SOURCE_CONFIG).map(([src, cfg]) => (
          <span key={src} className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <OtaMark source={src} size={22} />
            {cfg.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span className="w-5 h-5 rounded-md bg-gray-200 flex items-center justify-center">
            <Ban size={11} className="text-gray-600" />
          </span>
          Blocked
        </span>
      </div>

      {confirmBlock && (
        <BlockConfirmModal
          label={confirmBlock.label}
          dateFrom={confirmBlock.dateFrom}
          dateTo={confirmBlock.dateTo}
          onClose={() => { setConfirmBlock(null); setBlockError(""); }}
          onConfirm={confirmAndBlock}
          saving={blocking}
          error={blockError}
        />
      )}
      {showMultiBlock && (
        <MultiBlockModal
          rows={[...selectedIds].map((unitId) => ({ unitId, ...unitMeta[unitId] }))}
          onClose={() => { setShowMultiBlock(false); setMultiBlockError(""); }}
          onConfirm={confirmMultiBlock}
          saving={multiBlocking}
          error={multiBlockError}
        />
      )}
      {rateOpen && (
        <DayRatesSheet
          key={`${rateOpen.roomId}-${rateOpen.date}-${rateOpen.nights}`}
          date={rateOpen.date}
          initialNights={rateOpen.nights}
          roomId={rateOpen.roomId}
          authHeaders={authHeaders}
          onClose={() => setRateOpen(null)}
          onChanged={load}
        />
      )}
      <BookingModal booking={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
